import { Component, Input, OnInit, TemplateRef } from '@angular/core';
import { AppComponentName, ExportDataCriteria, ExportSaveAs, InterpolationInterval, InterpolationMethod, Message, MessageActionType, MessageType, Sensor, SensorReadingType, TimeZoneDisplay } from '@models';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { AppMessageService } from '@services/message.service';
import { SensorService } from '@services/sensor.service';
import { UserService } from '@services/user.service';
import { CommonShared } from 'app/shared/classes/common';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { TableLazyLoadEvent } from 'primeng/table';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

@Component({
  standalone: false,
  selector: 'table-readings-modal',
  templateUrl: './table-readings-modal.component.html',
  styleUrls: ['./table-readings-modal.component.scss']
})

export class TableReadingsModalComponent implements OnInit {

  @Input() sensors: Sensor[];
  @Input() sensorReadings: SensorReadingInfo[];
  @Input() sensorReadingTypes: SensorReadingType[];
  @Input() includeOrigValues: boolean;
  @Input() includeCalValues: boolean;
  // Needed to build the server export criteria (the backend re-queries readings).
  @Input() dateRange: DateRange;
  @Input() timeZone: TimeZoneDisplay;
  @Input() timeZoneOffset: number;

  modalDragOn: boolean = false;
  exportOptionsModalRef: BsModalRef;
  username = 'unknown';
  common = new CommonShared();

  // Export options, edited in the export-options modal. Mirrors the Chart Group
  // Readings page so the server produces the same CSV2 format.
  exportDataCriteria: ExportDataCriteria = {
    filename: 'exported-readings',
    saveAs: ExportSaveAs.CSV2,
    startDate: '',
    endDate: '',
    sensorReadingTypes: [],
    interpolateData: false,
    interpolateInterval: InterpolationInterval.I30,
    interpolateIntervalValue: 30,
    interpolateMethod: InterpolationMethod.Linear,
    ignoreOutliers: false,
    ignoreOutliersLessThan: undefined,
    ignoreOutliersGreaterThan: undefined,
    uuids: [],
    retrieveRawValues: false,
    includeGpsInfo: false,
    timeZoneDisplay: undefined,
    timeZoneOffset: 0,
    includeCalValues: true,
    includeOrigValues: false
  };

  sensorReadingInfosForPage: SensorReadingInfo[] = [];
  currentDataTablePageEvent: TableLazyLoadEvent = { first: 0, rows: 14 };
  currentDataTablePagePercent = 0;
  sensorReadingInfosCount = 0;

  readingsPerTablePage = 15;  // Options are 15, 50, 100, 500, 1000 via radio buttons

  sensorReadingsCols = [
    { field: 'name', header: 'Sensor', width: '18%' },
    { field: 'createdDate', header: 'Time Stamp', width: '18%' },
    { field: 'source', header: 'Tag', width: '17%' },
    { field: 'dataType', header: 'Reading Type', width: '17%' },
    { field: 'origValue', header: 'Value (orig)', width: '12%' },
    { field: 'value', header: 'Value (cal)', width: '12%' },
    { field: 'gps', header: 'GPS', width: '6%' }
  ];

  busyProcessing = false;

  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
              public _sensorService: SensorService, public _userService: UserService, public _appMessageService: AppMessageService) {
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
  
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  ngOnChanges() {
    this.updateReadingsTable(this.currentDataTablePageEvent);
  }


  updateReadingsTable(tablePageEvent: TableLazyLoadEvent) {
    this.currentDataTablePageEvent = tablePageEvent;

    if (this.sensorReadings == undefined) return; // || this.sensorReadings.length < 1) return;

    this.sensorReadingInfosForPage = JSON.parse(JSON.stringify(this.sensorReadings));
    this.sensorReadingInfosForPage.forEach(x => x.createdDate = moment(x.createdDate).toDate());

    const checkedReadingTypes = this.sensorReadingTypes.filter(x => x.checked).map(y => y.id);
    this.sensorReadingInfosForPage = this.sensorReadingInfosForPage.filter(x => checkedReadingTypes.includes(x.typeID));
    this.sensorReadingInfosCount = this.sensorReadingInfosForPage.length;
    this.sensorReadingInfosForPage = this.sensorReadingInfosForPage.slice(tablePageEvent.first, tablePageEvent.first + tablePageEvent.rows);

    let compareDate = moment().local().toDate();

    for (let index=0; index < this.sensorReadingInfosForPage.length; index++) {
      const s = this.sensorReadingInfosForPage[index];
      if (s.createdDate != undefined && s.createdDate.getTime() === compareDate.getTime()) {
        s.createdDate = undefined;
        s.tag = '';
      } else if (s.createdDate != undefined) {
        compareDate = s.createdDate;
      }
    }
  }


  // Open the export-options dialog, prefilling the criteria from the current
  // selections. Matches the Chart Group Readings export: the backend re-queries
  // the readings from this criteria and returns the CSV, so the export uses the
  // same CSV2 format. Only the reading types currently shown in the table
  // (checked) are exported.
  exportData(template: TemplateRef<any>) {
    if (this.sensorReadings == undefined || this.sensorReadings.length < 1) {
      this.toastr.warning('No readings to export');
      return;
    }

    this.exportDataCriteria.startDate = this.dateRange.startDate.format('YYYY-MM-DD HH:mm');
    this.exportDataCriteria.endDate = this.dateRange.endDate.format('YYYY-MM-DD HH:mm');
    this.exportDataCriteria.sensorReadingTypes = this.sensorReadingTypes.filter(x => x.checked).map(x => x.type);
    this.exportDataCriteria.timeZoneDisplay = this.timeZone;
    this.exportDataCriteria.timeZoneOffset = this.timeZoneOffset;
    this.exportDataCriteria.uuids = this.sensors.map(x => x.uuid);

    this.exportOptionsModalRef = this.modalService.show(template, { animated: false, backdrop: false, ignoreBackdropClick: true });
  }


  // Send the criteria to the backend, which builds the CSV (or JSON), then save
  // the returned text to a file.
  exportDataWithOptions() {
    if (this.exportDataCriteria.filename == '') {
      this.toastr.warning('Please provide a filename for the exported data');
      return;
    }
    this.exportOptionsModalRef.hide();

    this.exportDataCriteria.includeCalValues = this.includeCalValues;
    this.exportDataCriteria.includeOrigValues = this.includeOrigValues;
    this.exportDataCriteria.retrieveRawValues = this.includeOrigValues;

    switch (this.exportDataCriteria.interpolateInterval) {
      case InterpolationInterval.I15:
        this.exportDataCriteria.interpolateIntervalValue = 15;
        break;
      case InterpolationInterval.I30:
        this.exportDataCriteria.interpolateIntervalValue = 30;
        break;
      case InterpolationInterval.I60:
        this.exportDataCriteria.interpolateIntervalValue = 60;
        break;
      case InterpolationInterval.I1440:
        this.exportDataCriteria.interpolateIntervalValue = 1440;
        break;
      default:
        this.exportDataCriteria.interpolateIntervalValue = 15;
        break;
    }

    if (this.exportDataCriteria.interpolateData == false) {
      this.exportDataCriteria.interpolateIntervalValue = undefined;
    }

    this._sensorService.exportSensorReadingsAsText(this.exportDataCriteria).subscribe(
      results => {
        if (results != null) {
          if (results.length == 0) {
            this.toastr.warning('No data associated with the given sensors and types');
          } else {
            this.saveExportToFile(results);
          }
        } else {
          this.toastr.warning('No sensor readings found to export');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while attempting to export sensor data');
      });
  }


  saveExportToFile(fileContent: string) {
    const extension = (this.exportDataCriteria.saveAs == ExportSaveAs.JSON) ? '.json' : '.csv';
    this.saveData(fileContent, this.exportDataCriteria.filename + extension);

    this.createMessageData(MessageActionType.Edited, this.sensorReadings);
  }


  setInterpolateData(state: boolean) {
    this.exportDataCriteria.interpolateData = state;
  }


  saveData(dataString: string, fileName: string) {
    const blob = new Blob(['\uFEFF', dataString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  }


  changeReadingsPerTablePage(newPageSize: number) {
    //this.readingsPerTablePage = newPageSize;
    this.currentDataTablePageEvent.rows = newPageSize;
    this.updateReadingsTable(this.currentDataTablePageEvent);
  }


  createMessageData(msgActionType: MessageActionType, sensorReadings: SensorReadingInfo[]) {
    var sensorIds = sensorReadings.map(x => x.sensorID).filter((value, index, self) => self.indexOf(value) === index);
    var sensorNames = this.sensors.filter(x => sensorIds.includes(x.id)).map(y => this.common.getSensorDisplayName(y));
  
    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.Sensors,
      actionType: msgActionType,
      appComponentItem: "Analyze Sensor Readings",
      details: `${sensorReadings.length} exported from these sensors: ${sensorNames.join(', ')}`,
      userNotes: "",
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: null
    };

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      err => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }


}
